import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Target, Brain, Flame, Gauge } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import { Membership } from "@/components/Membership";
import { trackEvent, QUIZ_URL } from "@/lib/api";

const HERO_IMG = "https://images.pexels.com/photos/35918463/pexels-photo-35918463.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PORTRAIT_IMG = "/images/w2w-philosophy.webp";
const GRAVITY_IMG = "/images/w2w-behavioral-gravity.webp";
const FORCES_IMG = "/images/w2w-three-forces.webp";
const SWING_IMG = "/images/w2w-golden-hour-teaser.webp";
const SUNSET_IMG = "https://images.pexels.com/photos/6256832/pexels-photo-6256832.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CTAButton = ({ location, children, className = "" }) => (
  <motion.span whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="inline-block">
    <a
      href={QUIZ_URL}
      data-testid={`cta-${location}`}
      onClick={() => trackEvent("cta_click", { location })}
      className={`group inline-flex items-center gap-2 bg-volt text-obsidian font-display font-bold px-7 py-4 text-base hover:bg-white transition-colors duration-200 ${className}`}
    >
      {children}
      <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200" />
    </a>
  </motion.span>
);

const Chapter = ({ num, title, body, image, flip, caption }) => (
  <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center py-20 lg:py-28`}>
    <div className={`lg:col-span-7 relative ${flip ? "lg:order-2 lg:col-start-6" : ""}`}>
      <span aria-hidden="true" className="absolute -top-16 -left-2 font-display font-black text-[10rem] lg:text-[14rem] leading-none text-white/5 select-none">
        {num}
      </span>
      <Reveal>
        <Overline>Chapter {num}</Overline>
        <h3 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none mt-4 mb-6 relative">
          {title}
        </h3>
        <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-xl relative">{body}</p>
      </Reveal>
    </div>
    <Reveal className={`lg:col-span-4 ${flip ? "lg:order-1" : "lg:col-start-9"}`} delay={0.15}>
      <div className="border border-white/10 p-2 bg-ink">
        <img src={image} alt={title} loading="lazy" className="w-full aspect-[4/5] object-cover grayscale-[30%] hover:grayscale-0 transition-[filter] duration-700" />
        {caption && (
          <div data-testid={`chapter-${num}-caption`} className="px-4 py-3 border-t border-white/10 flex items-center gap-2.5">
            <span className="w-2 h-2 bg-volt shrink-0" />
            <p className="text-xs font-bold tracking-wide text-white/70">{caption}</p>
          </div>
        )}
      </div>
    </Reveal>
  </div>
);

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  useEffect(() => {
    document.title = "Wired 2 Win Golf — Master the Mental Game";
    trackEvent("page_view", { page: "home" });
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-1/4">
          <img src={HERO_IMG} alt="Sunrise over a golf course" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-[linear-gradient(to_top,#0A0A0A_0%,transparent_100%)]" />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-40 w-full">
          <Overline className="mb-6">
            <MaskedLine delay={0.1}>The Wired2Win Mental Game Assessment</MaskedLine>
          </Overline>
          <h1 className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-7xl lg:text-[7rem] uppercase">
            <MaskedLine delay={0.25}>Your swing</MaskedLine>
            <MaskedLine delay={0.4}>may not be</MaskedLine>
            <MaskedLine delay={0.55}><span className="text-volt">the problem.</span></MaskedLine>
          </h1>
          <Reveal delay={0.85} y={24}>
            <p className="mt-8 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed">
              Your wiring could be. Discover how your mind actually performs under pressure — with the free 30-question assessment trusted by golfers who are done guessing.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <CTAButton location="hero">Take the Free Assessment</CTAButton>
              <div className="flex items-center gap-6 text-xs text-white/50 font-semibold tracking-wide">
                <span data-testid="hero-stat-questions">30 questions</span>
                <span className="w-px h-4 bg-white/20" />
                <span data-testid="hero-stat-time">6 minutes</span>
                <span className="w-px h-4 bg-white/20" />
                <span data-testid="hero-stat-cost">Free forever</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 py-6 bg-obsidian overflow-hidden" data-testid="ethos-marquee">
        <Marquee speed={30} gradient={false}>
          {["Mindset", "Focus", "Composure", "Confidence", "Drive", "Wired 2 Win"].map((w) => (
            <span key={w} className="font-display font-black uppercase text-4xl md:text-6xl tracking-tight text-outline mx-8">
              {w} <span className="text-volt text-2xl align-middle mx-2">/</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* PROBLEM */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <Overline>The uncomfortable truth</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none mt-4">
              You practice the 10%. <span className="text-white/40">The other 90% decides your score.</span>
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7 lg:pt-14" delay={0.15}>
            <p className="text-white/60 text-base sm:text-lg leading-relaxed mb-6">
              Hours on the range. New driver every season. Lessons on repeat. Yet the same blow-up holes, the same three-putts under pressure, the same first-tee nerves.
            </p>
            <p className="text-white/60 text-base sm:text-lg leading-relaxed">
              Wired 2 Win Golf trains the part of your game that travels with you to every shot: <span className="text-white font-semibold">how you're wired</span> — your focus, confidence, composure, and drive. It starts with knowing your profile.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="mt-16 border border-white/10 p-2 bg-ink max-w-4xl" data-testid="flow-pressure-feature">
            <img src="/images/w2w-flow-pressure-teaser.webp" alt="Flow state versus pressure state — the same golfer, two wirings" loading="lazy" className="w-full object-cover" />
          </div>
        </Reveal>
      </section>

      {/* MANIFESTO */}
      <section className="border-t border-white/10 bg-ink/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
          <Reveal className="py-16 lg:py-20">
            <Overline>The manifesto</Overline>
            <h2 className="font-display text-4xl sm:text-6xl font-black tracking-tighter leading-none mt-4 max-w-3xl">
              Three beliefs. <span className="text-outline-volt">Zero excuses.</span>
            </h2>
          </Reveal>
          <Chapter
            num="01"
            title="Pressure is a skill, not a personality trait."
            body="Nobody is born clutch. The players who thrive on the 18th tee built that calm rep by rep — with routines, resets, and authentic self-understanding. It's trainable, and it starts with how you're wired."
            image={PORTRAIT_IMG}
          />
          <Chapter
            num="02"
            title="Your wiring has a pattern. Patterns can be rewritten."
            body="The way you react to a shank, a slow group, a must-make putt — it isn't random. The way you process instruction, approach practice, or choose playing partners is personal. It's a profile. Measure it honestly and you can train it deliberately. That's what the Wired2Win assessment reveals."
            image={GRAVITY_IMG}
            flip
          />
          <Chapter
            num="03"
            title="Small edges compound into trophies."
            body="One committed swing. One clean reset. One honest grade after the round. None of it looks like much on a Tuesday. Stacked over a season, it's the difference between hope and expectation."
            image={FORCES_IMG}
          />
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <Reveal>
          <Overline>What the assessment gives you</Overline>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none mt-4 mb-16 max-w-2xl">
            Six minutes in. A roadmap out.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Brain, t: "Your archetype", d: "Go For It, Organize To Optimize, Light It Up, or Feel It — the mental-game identity that runs your round.", id: "benefit-archetype" },
            { icon: Gauge, t: "Your G-O-L-F scorecard", d: "Raw scores across all four dimensions — with your Primary and Accent wiring called out.", id: "benefit-scorecard" },
            { icon: Target, t: "Your Mental Mulligan", d: "The reset move for when pressure cracks your pattern — borrowed from your diagonal opposite.", id: "benefit-growth" },
            { icon: Flame, t: "Weekly wiring", d: "One sharp mindset drill in your inbox each week. Short, usable, built for real rounds.", id: "benefit-weekly" },
          ].map((b, i) => (
            <Reveal key={b.id} delay={i * 0.1}>
              <motion.div
                data-testid={b.id}
                whileHover={{ y: -6 }}
                transition={{ duration: 0.25 }}
                className="border border-white/10 bg-ink p-8 h-full hover:border-volt/60 transition-colors duration-300"
              >
                <b.icon className="w-7 h-7 text-volt mb-6" />
                <h3 className="font-display text-2xl font-bold tracking-tight mb-3">{b.t}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{b.d}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-14" delay={0.1}>
          <CTAButton location="benefits">Get My Profile — It's Free</CTAButton>
        </Reveal>
      </section>

      {/* BOOK PROMO */}
      <section className="border-t border-white/10 bg-ink/40" data-testid="book-promo">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-4">
            <div className="border border-white/10 bg-obsidian p-2 max-w-xs">
              <img
                src="/images/boc-cover-3d-cropped.webp"
                alt="Back on Course: Drive Business Performance Through Golf — book cover"
                loading="lazy"
                data-testid="book-cover-image"
                className="w-full aspect-[2/3] object-cover"
              />
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7 lg:col-start-6" delay={0.15}>
            <Overline>From the founder's desk</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none mt-4 mb-6">
              The book that started <span className="text-volt">the movement.</span>
            </h2>
            <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-xl mb-4">
              <span className="text-white font-semibold">Back on Course: Drive Business Performance Through Golf</span> is Dave Bisbee's essential read on turning the game into a tool for leadership, decision-making, and results — on the course and in the boardroom.
            </p>
            <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-xl mb-8">
              The same thinking powers every question in the Wired2Win assessment.
            </p>
            <motion.span whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="inline-block">
              <a
                href="https://backoncoursegolf.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-buy-cta"
                onClick={() => trackEvent("cta_click", { location: "book-promo" })}
                className="group inline-flex items-center gap-2 border border-volt text-volt font-display font-bold px-7 py-4 text-base hover:bg-volt hover:text-obsidian transition-colors duration-200"
              >
                Get the Book
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200" />
              </a>
            </motion.span>
          </Reveal>
        </div>
      </section>

      {/* QUIZ TEASER */}
      <section className="relative border-y border-white/10 overflow-hidden" data-testid="quiz-teaser">
        <img src={SWING_IMG} alt="Golfer overlooking a golden-hour fairway, constellation wiring underfoot" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-right" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#0A0A0A_5%,rgba(10,10,10,0.82)_45%,rgba(10,10,10,0.25)_85%)]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-28 lg:py-40">
          <Reveal>
            <Overline>The first step — 30 questions, one honest mirror</Overline>
            <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-7xl uppercase mt-6 max-w-4xl">
              Why do you play great one day… <span className="text-volt">and struggle the next?</span>
            </h2>
            <p className="mt-8 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed">
              No right answers. No swing tips. You get a full report: what drives you, your natural strengths, the conditions you need to thrive — and how you crack under stress.
            </p>
            <p className="mt-6 text-volt font-display font-bold tracking-wide text-sm uppercase">
              Know your wiring. Master your game.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <CTAButton location="teaser">Start the Assessment</CTAButton>
              <a
                href="/downloads/wired2win-sample-report.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sample-report-link"
                onClick={() => trackEvent("sample_report_view")}
                className="text-white/70 hover:text-volt text-sm font-semibold underline underline-offset-4 decoration-white/30 hover:decoration-volt transition-colors"
              >
                See a sample report
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MEMBERSHIP */}
      <Membership />

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28 lg:py-36 text-left">
        <Reveal>
          <h2 className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-7xl uppercase max-w-4xl">
            Stop guessing.<br /><span className="text-outline-volt">Get wired.</span>
          </h2>
          <div className="mt-10">
            <CTAButton location="final">Take the Free Assessment</CTAButton>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
